import { useState, useEffect, useCallback } from "react";
import { useAuth, apiClient, useTheme } from "@/App";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  GraduationCap,
  ArrowLeft,
  CheckCircle2,
  Sun,
  Moon,
  Settings,
  LogOut,
  CreditCard,
  Sparkles,
  Loader2,
  Check,
  Calendar,
  Target,
} from "lucide-react";
import { motion } from "framer-motion";

const Subscription = () => {
  const { user, logout, refreshUser } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);

  const pollPaymentStatus = useCallback(async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setCheckingPayment(false);
      toast.error("Payment verification timed out. Please refresh the page.");
      return;
    }

    try {
      const response = await apiClient.get(`/payments/status/${sessionId}`);
      
      if (response.data.payment_status === "paid") {
        setCheckingPayment(false);
        toast.success("Payment successful! Your subscription is now active.");
        await refreshUser();
        navigate("/subscription", { replace: true });
        return;
      } else if (response.data.status === "expired") {
        setCheckingPayment(false);
        toast.error("Payment session expired. Please try again.");
        return;
      }

      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error("Error checking payment status:", error);
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    }
  }, [refreshUser, navigate]);

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (sessionId) {
      setCheckingPayment(true);
      pollPaymentStatus(sessionId);
    }
  }, [searchParams, pollPaymentStatus]);

  const handleSubscribe = async () => {
    setLoading(true);
    try {
      const originUrl = window.location.origin;
      const response = await apiClient.post("/payments/create-checkout", {
        origin_url: originUrl,
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error("Error creating checkout:", error);
      toast.error("Failed to start checkout. Please try again.");
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const isSubscribed = user?.subscription_status === "active";

  return (
    <div className="min-h-screen bg-background noise-bg pb-20 md:pb-8">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border">
        <div className="container mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/dashboard")}
                className="rounded-full"
                data-testid="back-btn"
                aria-label="Go back"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-2">
                <GraduationCap className="w-8 h-8 text-primary" />
                <span className="font-bold text-xl font-outfit hidden sm:block">Admit List</span>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className="rounded-full"
                data-testid="theme-toggle-btn"
                aria-label="Toggle theme"
              >
                {theme === "dark" ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative h-10 w-10 rounded-full" data-testid="user-menu-btn">
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={user?.picture} alt={user?.name} />
                      <AvatarFallback>{user?.name?.[0] || "U"}</AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56" align="end" forceMount>
                  <div className="flex items-center justify-start gap-2 p-2">
                    <div className="flex flex-col space-y-1 leading-none">
                      <p className="font-medium">{user?.name}</p>
                      <p className="text-xs text-muted-foreground">{user?.email}</p>
                    </div>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/subscription")} data-testid="subscription-menu-item">
                    <CreditCard className="mr-2 h-4 w-4" />
                    Subscription
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="settings-menu-item">
                    <Settings className="mr-2 h-4 w-4" />
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} data-testid="logout-menu-item">
                    <LogOut className="mr-2 h-4 w-4" />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 md:px-8 py-8">
        {checkingPayment ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 className="w-12 h-12 animate-spin text-primary mb-4" />
            <h2 className="text-xl font-semibold font-outfit mb-2">Verifying your payment...</h2>
            <p className="text-muted-foreground">Please wait while we confirm your subscription.</p>
          </div>
        ) : (
          <>
            <motion.div
              className="text-center mb-12"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              <h1 className="text-3xl md:text-4xl font-bold tracking-tight font-outfit mb-4">
                {isSubscribed ? "Welcome to Admit List" : "Get Full Access"}
              </h1>
              <p className="text-muted-foreground max-w-lg mx-auto">
                {isSubscribed
                  ? "You have lifetime access to all features. Start tracking your college applications!"
                  : "One payment. Lifetime access to AI-powered college research and deadline tracking."}
              </p>
            </motion.div>

            {isSubscribed ? (
              <motion.div
                className="max-w-md mx-auto"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.1 }}
              >
                <Card className="border-emerald-200 dark:border-emerald-900/50">
                  <CardContent className="p-6 text-center">
                    <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mx-auto mb-4">
                      <CheckCircle2 className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />
                    </div>
                    <h3 className="text-xl font-semibold font-outfit mb-2">Full Access Unlocked</h3>
                    <p className="text-muted-foreground mb-4">
                      You have lifetime access to all Admit List features.
                    </p>
                    <div className="flex items-center justify-center gap-2 text-sm text-emerald-600 dark:text-emerald-400 mb-6">
                      <Sparkles className="w-4 h-4" />
                      All features unlocked
                    </div>
                    <Button
                      className="w-full rounded-full"
                      onClick={() => navigate("/dashboard")}
                      data-testid="go-to-dashboard-btn"
                    >
                      Go to Dashboard
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            ) : (
              <motion.div
                className="max-w-md mx-auto"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.1 }}
              >
                <Card className="relative overflow-hidden border-2 border-primary">
                  <div className="absolute top-0 left-0 right-0 h-1 bg-primary" />
                  <CardContent className="p-8 text-center">
                    <h3 className="text-xl font-bold font-outfit mb-2">Admit List</h3>
                    <p className="text-muted-foreground mb-6">Everything you need for your college applications</p>
                    <div className="mb-6">
                      <span className="text-5xl font-bold font-outfit">$19.99</span>
                      <span className="text-muted-foreground ml-2">one time</span>
                    </div>
                    <ul className="text-left space-y-3 mb-8">
                      {[
                        "Unlimited college tracking",
                        "AI-powered deadline research",
                        "Interactive calendar view",
                        "School comparison charts",
                        "Email deadline reminders",
                        "CSV data export",
                        "Share acceptance cards",
                        "Lifetime access — no recurring fees",
                      ].map((feature, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm">
                          <Check className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <Button
                      className="w-full rounded-full text-base"
                      size="lg"
                      onClick={() => handleSubscribe()}
                      disabled={loading}
                      data-testid="pay-now-btn"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        "Pay $19.99 — Get Full Access"
                      )}
                    </Button>
                    <p className="text-xs text-muted-foreground mt-4">Secure payment via Stripe</p>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* FAQ Section */}
            <motion.div
              className="mt-16 max-w-2xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.3 }}
            >
              <h2 className="text-2xl font-semibold font-outfit text-center mb-8">
                Frequently Asked Questions
              </h2>
              <div className="space-y-4">
                <Card>
                  <CardContent className="p-4">
                    <h4 className="font-semibold mb-2">Is this a one-time payment?</h4>
                    <p className="text-sm text-muted-foreground">
                      Yes! Pay once and get lifetime access to all Admit List features. No subscriptions, no recurring charges.
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <h4 className="font-semibold mb-2">What do I get with my purchase?</h4>
                    <p className="text-sm text-muted-foreground">
                      Unlimited college tracking, AI-powered deadline research for 200+ schools, calendar view, comparison charts, email reminders, CSV export, and shareable acceptance cards.
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <h4 className="font-semibold mb-2">How accurate is the AI research?</h4>
                    <p className="text-sm text-muted-foreground">
                      Our AI uses GPT-5.2 to research the latest college information. While we strive for accuracy, we recommend verifying deadlines on official college websites.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </motion.div>
          </>
        )}
      </main>

      {/* Mobile Bottom Nav */}
      <nav className="mobile-nav p-2">
        <div className="flex items-center justify-around">
          <Button
            variant="ghost"
            className="flex-1 flex flex-col items-center gap-1 h-auto py-2"
            onClick={() => navigate("/dashboard")}
            data-testid="mobile-nav-dashboard"
          >
            <GraduationCap className="w-5 h-5" />
            <span className="text-xs">Dashboard</span>
          </Button>
          <Button
            variant="ghost"
            className="flex-1 flex flex-col items-center gap-1 h-auto py-2"
            onClick={() => navigate("/calendar")}
            data-testid="mobile-nav-calendar"
          >
            <Calendar className="w-5 h-5" />
            <span className="text-xs">Calendar</span>
          </Button>
          <Button
            variant="ghost"
            className="flex-1 flex flex-col items-center gap-1 h-auto py-2"
            onClick={() => navigate("/categories")}
            data-testid="mobile-nav-categories"
          >
            <Target className="w-5 h-5" />
            <span className="text-xs">Categories</span>
          </Button>
          <Button
            variant="ghost"
            className="flex-1 flex flex-col items-center gap-1 h-auto py-2"
            onClick={() => navigate("/settings")}
            data-testid="mobile-nav-settings"
          >
            <Settings className="w-5 h-5" />
            <span className="text-xs">Settings</span>
          </Button>
        </div>
      </nav>
    </div>
  );
};

export default Subscription;
